<template>
  <div
    :class="[
      'input-wrapper',
      { 'input_error': error },
      { 'input_disabled': disabled },
      { 'input-wrapper__textarea': isTextarea }
    ]"
  >
    <div class="input__field-container">
      <Component
        v-if="isTel"
        :is="isTextarea ? 'textarea' : 'input'"
        :id="id"
        ref="input"
        v-bind="$attrs"
        v-mask="inputMask"
        :value="currentValue"
        :class="['input__field', { textarea: isTextarea }]"
        :tabindex="disabled ? -1 : 1"
        @input="onInput($event.target.value)"
        @focus="error = false"
        placeholder=" "
      />

      <Component
        v-else
        :is="isTextarea ? 'textarea' : 'input'"
        :id="id"
        ref="input"
        v-bind="$attrs"
        :value="currentValue"
        :class="['input__field', { textarea: isTextarea }]"
        :tabindex="disabled ? -1 : 1"
        :maxlength="max"
        @input="onInput($event.target.value)"
        @keydown="onKeyDown"
        @focus="handleFocus"
        @blur="handleBlur"
        @paste="onPaste"
        placeholder=" "
      />

      <label v-if="label" :for="id" class="input__label">
        <span v-if="labelHtml" v-html="label"></span>
        <span v-else>{{ label }}</span>
        <span v-if="required" class="input__label-required">*</span>
      </label>
    </div>

    <Transition name="slide-from-top">
      <span v-if="error && (errorMsg || errorMsgLocal)" class="input__error-msg">
        {{ errorMsg || errorMsgLocal }}
        <ErrorIcon />
      </span>
    </Transition>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { mask, tokens } from 'vue-the-mask'
  import ErrorIcon from '@/assets/img/icons/error.svg?inline'
  import { isMd } from '@/config'

  export default {
    name: 'BaseInput',
    directives: { mask },
    components: {
      ErrorIcon
    },
    inheritAttrs: false,
    props: {
      value: {
        type: String,
        default: ''
      },
      fieldType: {
        type: String,
        default: ''
      },
      label: {
        type: String,
        default: ''
      },
      max: {
        type: String,
        default: '9999'
      },
      errorMsg: {
        type: String,
        default: ''
      },
      disabled: {
        type: Boolean,
        default: false
      },
      defaultValue: {
        type: String,
        default: ''
      },
      labelHtml: {
        type: Boolean,
        default: false
      },
      onlyUppercase: {
        type: Boolean,
        default: false
      },
      customMessageText: {
        type: String,
        default: ''
      },
      validatePatternState: {
        type: Boolean,
        default: false
      },
      validatePatternVin: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        error: null,
        currentValue: '',
        errorMsgLocal: ''
      }
    },
    computed: {
      ...mapGetters(['getPhoneType']),
      id () {
        return `input-${this._uid}`
      },
      required () {
        return 'required' in this.$attrs && this.$attrs.required
      },
      inputMask () {
        if (this.getPhoneType === '+373') {
          return this.$attrs.type === 'tel' ? '+###########' : ''
        } else {
          return this.$attrs.type === 'tel' ? '+380-##-###-##-##' : ''
        }
      },
      isTextarea () {
        return this.$attrs.type === 'textarea'
      },
      isTel () {
        return this.$attrs.type === 'tel'
      }
    },
    watch: {
      value (val) {
        if (val !== this.currentValue) {
          this.currentValue = val
          this.$nextTick(() => {
            if (this.$refs.input) {
              this.$refs.input.value = val
            }
          })
        }
        if (val === '') {
          this.error = false
          this.errorMsgLocal = ''
        }
      },
      defaultValue: {
        handler () {
          this.onInput(this.defaultValue)
          this.$refs.input.value = this.defaultValue
        }
      },
      errorMsg: {
        handler (msg) {
          this.error = !!msg
        },
        immediate: true
      }
    },
    created () {
      this.currentValue = this.value || this.defaultValue
    },
    mounted () {
      tokens.X = { pattern: /[+\d]/, transform: v => v }
    },
    methods: {
      handleFocus (e) {
        if (this.validatePatternState && e.target.value.length > 0) {
          return
        }
        this.error = false
      },
      handleBlur (e) {
        if (this.validatePatternState && e.target.value.length === 0) {
          this.error = false
        }
      },
      onPaste (event) {
        if (!this.validatePatternState && !this.validatePatternVin) return

        event.preventDefault()

        const pastedData = (event.clipboardData || window.clipboardData).getData('text')
        if (!pastedData || pastedData.trim() === '') return
        const input = event.target
        const start = input.selectionStart
        const end = input.selectionEnd

        let allowedRegex

        if (this.validatePatternVin) {
          allowedRegex = /^[A-Za-z0-9]$/i
        }

        if (this.validatePatternState) {
          const latin = /^[A-Za-z0-9\s]$/
          const cyrillic = /^[А-Яа-яЁёЇїІіЄєҐґ0-9\s]$/

          allowedRegex = char => latin.test(char) || cyrillic.test(char)
        }

        let result = ''

        for (const char of pastedData) {
          const isValid = typeof allowedRegex === 'function'
            ? allowedRegex(char)
            : allowedRegex.test(char)

          if (!isValid) break
          result += char
        }

        const newValue =
          input.value.slice(0, start) +
          result +
          input.value.slice(end)

        const finalValue = newValue.slice(0, this.max)

        if (!finalValue) return
        this.onInput(finalValue)

        this.$nextTick(() => {
          input.value = this.currentValue
          const cursor = this.currentValue.length
          input.setSelectionRange(cursor, cursor)
        })
      },
      onKeyDown (event) {
        if (this.validatePatternState) {
          if (event.key === ' ' || event.code === 'Space') {
            event.preventDefault()
          }
        }
        if (this.validatePatternVin) {
          if (
            event.ctrlKey ||
            event.metaKey ||
            event.altKey
          ) {
            return
          }

          const allowedKeys = [
            'Backspace', 'Delete',
            'ArrowLeft', 'ArrowRight',
            'Tab', 'Enter',
            'Home', 'End'
          ]

          if (allowedKeys.includes(event.key)) return

          if (!/^[A-Za-z0-9]$/.test(event.key)) {
            event.preventDefault()
          }
        }
      },
      onInput (value) {
        if (this.fieldType === 'number') {
          value = value.replace(/[^\d]/g, '').slice(0, 8)
          this.currentValue = value
          this.$refs.input.value = this.currentValue
          this.$emit('input', this.currentValue)
          return
        }

        if (this.onlyUppercase) {
          value = value.toUpperCase()
        }

        if (this.validatePatternState || this.validatePatternVin) {
          value = value.replace(/\s+/g, '')
        }

        if (this.validatePatternVin) {
          value = value.replace(/[^A-Za-z0-9]/g, '')
        }

        if (this.validatePatternState) {
          const max = isMd ? 6 : 8
          const latinRegex = /^[A-Z0-9]*$/
          const cyrillicRegex = /^[А-ЯЁЇІЄҐ0-9]*$/

          if (
            value.length !== max ||
            (!latinRegex.test(value) && !cyrillicRegex.test(value))
          ) {
            this.error = true
            const message = this.customMessageText
            this.errorMsgLocal = message
            this.$emit('update:errorCustom', message)
          } else {
            this.error = false
            this.errorMsgLocal = ''
            this.$emit('update:errorCustom', '')
          }
        }

        this.currentValue = value
        this.$emit('input', this.currentValue)
      }
    }
  }
</script>

<style lang="scss">
  $padding-for-error: 28px;

  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    box-shadow: 0 0 0 30px $c-gray-light inset !important;
  }

  .input {
    &-wrapper {
      position: relative;
      min-width: 290px;
      padding-bottom: $padding-for-error;
    }

    &_disabled {
      pointer-events: none;

      //.input__field {
      //  background: $c-gray-4;
      //}
    }

    &__label {
      position: absolute;
      z-index: 1;
      top: 50%;
      left: 20px;
      transform: translateY(-50%);
      display: inline-block;
      font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
      font-size: 16px;
      line-height: 1.2;
      color: $c-gray-7;
      transition: 0.3s;

      span {
        color: $c-gray-7;

        br {
          display: none;
        }
      }

      &-required {
        position: relative;
        top: -3px;
        color: $c-errorDark !important;
        font-size: 13px;
        right: -8px;
      }
    }

    &__field {
      display: block;
      width: 100%;
      padding: 27.5px 16px 8px;
      font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
      font-size: 16px;
      line-height: 1;
      //background-color: $c-gray-light;
      outline: none;
      caret-color: $c-gray-dark;
      appearance: none !important;
      -webkit-appearance: none !important;
      border-radius: 0 !important;
      transition: 0.3s;
      border: 1px solid $c-gray-4;

      &:invalid {
        box-shadow: none;
      }

      &.textarea {
        padding-top: 28px;
        padding-bottom: 20px;
        overflow: auto;
        line-height: 1.4;
        resize: none;

        + .input__label {
          top: 28px;
        }

        @include custom-scrollbar-inputs();
      }

      &-container {
        position: relative;
      }

      @include hover {
        border: 1px solid $c-gray-dark;
      }

      &:focus {
        border: 1px solid $c-gray-dark;
      }

      &:focus ~ .input__label,
      &:not(:placeholder-shown) ~ .input__label {
        top: 11px;
        left: 20px;
        font-size: 13px;
        transform: translateY(0);

        @include mobile {
          font-size: 11px;
        }

        span {
          color: $c-gray-7;
        }
      }
    }

    &__error-msg {
      display: flex;
      align-items: center;
      justify-content: space-between;
      position: absolute;
      left: 0;
      font-size: 12px;
      color: $c-errorDark;
      height: 20px;
      bottom: 2px;
      width: calc(100% - 30px);

      @include tablet {
        padding-top: 3px;
        position: static;
        min-height: 30px;
        height: auto;

        &.slide-from-top-enter-active,
        &.slide-from-top-leave-active {
          transition: none;
        }

        &.slide-from-top-enter,
        &.slide-from-top-leave-to {
          opacity: 1;
          transform: none;
        }
      }

      img,
      svg {
        display: flex;
        min-width: 14px;
        min-height: 15px;
        width: 14px;
        height: 15px;
        object-fit: contain;
      }
    }

    &_error {
      .input {
        &__field {
          border: 1px solid $c-errorDark;
        }
      }
    }

    &-wrapper__textarea {
      .input__field-container {
        position: relative;

        &:after {
          pointer-events: none;
          content: '';
          position: absolute;
          width: calc(100% - 10px);
          height: 30px;
          background: #fff;
          top: 1px;
          left: 1px;
        }
      }
    }
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input[type=number] {
    -moz-appearance: textfield;
  }
</style>
